import Card from 'components/Layout/Card';
import ExportButton from 'components/Layout/ExportButton';
import FilterDropdown from 'components/Layout/FilterDropdown';
import Table from 'components/Layout/Table';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  exportSubmittedAbstracts,
  fetchConferencesList,
  fetchDetailedViewData,
  getSourceList,
  setSubmittedAbstractsDetailedView,
} from 'store/slices';
import {
  getOtherStatus,
  getStatus,
  objectToFormData,
  titleCaseString,
} from 'utils/helpers';
import ActionFilter from '../ActionFilter/ActionFilter';
import { limit, wpChatStatus } from 'utils/constants';
import { useNavigate, useParams } from 'react-router-dom';
import CommentAddView from '../CommentAddView';
import CheckBox from 'components/form/CheckBox';
import Button from 'components/form/Button';

const leadColor = (values = '') => {
  if (values) {
    if (values === 'Warm') {
      return 'color-a700';
    } else if (values === 'Hot') {
      return 'color-4646';
    } else if (values === 'Cold') {
      return 'color-7cea';
    } else {
      return 'color-black';
    }
  }
};

const whatsappChatColor = (values = '') => {
  if (values) {
    const lowerCaseValue = values.toLowerCase();

    if (lowerCaseValue.includes('unread')) {
      return 'color-3dcd text-14-400';
    } else if (lowerCaseValue.includes('read')) {
      return 'color-1717 text-14-400';
    } else if (lowerCaseValue.includes('not seen')) {
      return 'color-5110 text-14-400';
    } else {
      return 'color-black';
    }
  }
  return 'color-black';
};

const DetailedView = ({ showActionButtons, setIsCallConnect }) => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    membershipList,
    countryList,
    conferencesList,
    userDetails,
    submittedAbstractsDetailedView: submittedAbstracts,
  } = useSelector((state) => ({
    membershipList: state.global.membershipList,
    countryList: state.global.countryList,
    conferencesList: state.global.conferencesList,
    userDetails: state.student.userDetails,
    submittedAbstractsDetailedView: state.global.submittedAbstractsDetailedView,
  }));
  const { memberType, moduleType, eventId } = params;
  const [sourceList, setSourceList] = useState([]);
  const [abstractId, setAbstractId] = useState(null);
  const [tableList, setTableList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchPayload, setSearchPayload] = useState({
    paper_id: '',
    paper_title: '',
    source: '',
    member_id: '',
    member_type: '',
    author_name: '',
    author_email: '',
    conference: '',
    session: '',
    date_of_submission: '',
    university: '',
    country: '',
    status: '',
    statusType: '',
    statuss: '',
    lead: '',
    remarks: '',
    abstract_id_or_title: '',
  });
  const [filterData, setFilterData] = useState({
    total: 0,
    offset: submittedAbstracts ? submittedAbstracts : 0,
    limit: limit,
    membership_plan_id: '',
    event_id: eventId ? eventId : '',
    temporary_users: '',
  });

  const getDetailedViewData = async (obj) => {
    let formData = new FormData();
    if (obj?.statusValue) {
      obj?.statusValue?.forEach((value) => {
        formData.append(
          `status[${obj?.statusType?.toLowerCase()}][${value}]`,
          value
        );
      });
    }
    const keysToOmit = ['statusType', 'statuss', 'statusValue', 'status'];
    for (const key in obj) {
      if (!keysToOmit.includes(key)) {
        formData.append(key, obj[key]);
      }
    }
    const response = await dispatch(fetchDetailedViewData(formData));
    setTableList(response?.data?.abstracts || []);
    setFilterData({
      ...obj,
      total: response?.data?.result_count || 0,
    });
    setIsLoading(false);
  };

  const handelChangeSearch = (searchData) => {
    setIsLoading(true);
    let newData = filterData;
    setSearchPayload(searchData);
    newData = { ...newData, ...searchData, offset: 0 };
    setFilterData(newData);
    getDetailedViewData(newData);
  };

  const handelChangePagination = (offset) => {
    setIsLoading(true);
    let newData = { ...filterData, ...searchPayload };
    newData = { ...newData, offset: offset };
    dispatch(setSubmittedAbstractsDetailedView(offset));
    setFilterData(newData);
    getDetailedViewData(newData);
  };

  const getConferenceList = async () => {
    await dispatch(fetchConferencesList());
  };

  const handelChangeFilter = (val) => {
    setIsLoading(true);
    let newData = { ...filterData, ...searchPayload };
    newData = { ...newData, membership_plan_id: val, offset: 0 };
    setFilterData(newData);
    getDetailedViewData(newData);
  };

  const handleToggle = (id) => {
    if (abstractId === id) {
      setAbstractId(null);
    } else {
      setAbstractId(id);
    }
  };

  const fetchSourceList = async () => {
    const response = await dispatch(
      getSourceList(objectToFormData({ type: 'paper' }))
    );
    setSourceList(response?.data || []);
  };

  useEffect(() => {
    getDetailedViewData({ ...filterData, ...searchPayload });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPayload]);

  useEffect(() => {
    getConferenceList();
    fetchSourceList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRedirect = (paperId) => {
    let newModuleType = '';

    if (!moduleType && window.location.pathname.includes('my-profile')) {
      newModuleType = 'my-profile';
    } else {
      newModuleType = moduleType;
    }
    navigate(`/${memberType}/${newModuleType}/new-submitted-papers/${paperId}`);
  };

  const { view: viewAccess, create: createAccess } =
    userDetails?.role_data?.find(
      (item) => item?.permission_name === 'Event Management'
    )?.sub_permission || {};

  let header = [
    {
      isSearch: false,
      searchInputName: 'name',
      title: 'S.No',
    },
    {
      isSearch: true,
      searchInputName: 'paper_id',
      title: 'Paper ID',
    },
    {
      isSearch: true,
      searchInputName: 'paper_title',
      title: 'Paper Title',
    },
    {
      isSearch: true,
      searchInputName: 'paper_type',
      title: 'Paper Type',
    },
    {
      isSearch: true,
      isSearchDropdown: true,
      dropdownOptions: {
        options: [{ id: 1, value: 'Yes' }, { id: 2, value: 'No' }],
        key: 'id',
        value: 'value',
      },
      searchInputName: 'add_on_services',
      title: <div className="text-nowrap ms-4 me-4">Add On</div>,
    },
    {
      isSearch: true,
      isSearchDropdown: true,
      dropdownOptions: {
        options: sourceList,
        key: 'source',
        value: 'source',
      },
      searchInputName: 'source',
      title: <div className="text-nowrap ms-4 me-4">Source</div>,
    },
    {
      isSearch: true,
      isSearchDropdown: true,
      dropdownOptions: { options: wpChatStatus, key: 'name', value: 'name' },
      searchInputName: 'whatsapp_chat',
      title: <div className="text-nowrap">WhatsApp Chat</div>,
    },

    {
      isSearch: true,
      searchInputName: 'member_id',
      title: <div>Member ID</div>,
    },
    {
      isSearch: true,
      isSearchDropdown: true,
      dropdownOptions: { options: membershipList, key: 'name', value: 'name' },
      searchInputName: 'member_type',
      title: <div className="text-nowrap">Member Type</div>,
    },
    {
      isSearch: true,
      searchInputName: 'author_name',
      title: 'Author',
    },
    {
      isSearch: true,
      searchInputName: 'author_email',
      title: <div className="text-nowrap">Author Email</div>,
    },
    {
      isSearch: true,
      isSearchDropdown: true,
      dropdownOptions: {
        options: conferencesList || [],
        key: 'event_name',
        value: 'event_name',
      },
      searchInputName: 'conference',
      title: <div className="text-nowrap">Conference Name</div>,
    },
    // {
    //   isSearch: true,
    //   isSearchDropdown: true,
    //   dropdownOptions: { options: [], key: "name", value: "name" },
    //   searchInputName: "session",
    //   title: "Session",
    // },
    {
      isSearch: true,
      isDatePicker: true,
      searchInputName: 'date_of_submission',
      title: 'Date of Submission',
    },
    {
      isSearch: true,
      searchInputName: 'university',
      title: 'University',
    },
    {
      isSearch: true,
      isSearchDropdown: true,
      dropdownOptions: {
        options: countryList || [],
        key: 'id',
        value: 'country',
      },
      searchInputName: 'country',
      title: 'Country',
    },
    {
      isSearch: true,
      isPaperStatusFilter: true,
      handlePaperStatusChange: (e) => {
        setSearchPayload((prev) => {
          return {
            ...prev,
            statusType: e.category,
            statuss: e.subCategory,
            statusValue: e.subCategoryValue,
          };
        });
      },
      searchInputName: 'status',
      title: 'Status',
    },
    {
      isSearch: true,
      isSearchDropdown: true,
      dropdownOptions: {
        options: [
          // { name: "Pending" },
          { name: 'Cold' },
          { name: 'Hot' },
          { name: 'Warm' },
        ],
        key: 'name',
        value: 'name',
      },
      searchInputName: 'lead',
      title: 'Lead',
    },
    {
      isSearch: true,
      searchInputName: 'remarks',
      title: 'Remarks',
    },
    {
      isSearch: false,
      searchLable: '',
      title: 'Action',
    },
  ];

  const rowData = [];
  tableList?.forEach((elem, index) => {
    let obj = [
      {
        value: <span>{filterData?.offset + index + 1}</span>,
      },
      {
        value: (
          <span
            className="pointer color-new-car"
            onClick={() => {
              handleRedirect(elem?.id);
            }}
          >
            {elem?.paperId}
          </span>
        ),
      },
      {
        value: (
          <div style={{ width: '200px' }}>
            {titleCaseString(elem?.paperTitle)}
          </div>
        ),
      },
      {
        value: <div style={{ width: '100px' }}>{elem?.paper_type || '-'}</div>,
      },
      {
        value: <span >{elem?.add_on_services || '-'}</span>,
      },
      {
        value: <span>{elem?.submitted_from ? elem?.submitted_from : '-'}</span>,
      },
      {
        value: (
          <div
            className={whatsappChatColor(elem?.whatsapp_chat)}
            style={{ width: '100px' }}
          >
            {elem?.whatsapp_chat ? elem?.whatsapp_chat : '-'}
          </div>
        ),
      },
      {
        value: <div className="text-nowrap">{elem?.memberId || '-'}</div>,
      },
      {
        value: <div>{elem?.membershipCategory || '-'}</div>,
      },
      {
        value: (
          <div className="text-14-500 color-new-car" style={{ width: '150px' }}>
            {elem?.authorName
              || '-'}
          </div>
        ),
      },
      {
        value: <span className="text-14-500">{elem?.authorEmail || '-'}</span>,
      },
      {
        value: <div style={{ width: '200px' }}>{elem?.conference || '-'}</div>,
      },
      // {
      //   value: <div style={{ width: "170px" }}>{elem?.session}</div>,
      // },
      {
        value: <div style={{ width: '170px' }}>{elem?.date_of_submission || '-'}</div>,
      },
      {
        value: <div style={{ width: '170px' }}>{elem?.university || '-'}</div>,
      },
      {
        value: <div style={{ width: '170px' }}>{elem?.country || '-'}</div>,
      },
      {
        value: (
          <div style={{ width: '100px' }}>
            {searchPayload?.statusType === 'Reviewer'
              ? getOtherStatus(elem?.submitted_status)
              : getStatus(elem?.submitted_status)}
          </div>
        ),
      },
      {
        value: (
          <div className={leadColor(elem?.lead)} style={{ width: '100px' }}>
            {elem?.lead || '-'}
          </div>
        ),
      },
      {
        value: <div style={{ width: '200px' }}>{elem?.remark || '-'}</div>,
      },
      {
        value: (
          <span className="action-icon-buttons align-items-center">
            <>
              {userDetails?.call_integration === '1' && (
                <Button
                  btnStyle="custom-hover-button"
                  icon={<i className="bi bi-telephone pointer" />}
                  className="me-2"
                  onClick={() => {
                    setIsCallConnect(elem);
                  }}
                />
              )}

              {/* {viewAccess === '1' && (
                <i
                  className="bi bi-eye pointer color-new-car"
                  onClick={() => {
                    handleRedirect(elem?.id);
                  }}
                />
              )} */}
              {viewAccess === '1' && (
                <Button
                  btnStyle="custom-hover-button"
                  icon={<i className="bi bi-eye pointer" />}
                  onClick={() => {
                    handleRedirect(elem?.id);
                  }}
                />
              )}

              {showActionButtons && createAccess === '1' && (
                <>
                  <CommentAddView
                    icon={
                      <i className="bi bi-chat-right-text position-relative" />
                    }
                    elem={elem}
                    handelSuccess={() => {
                      getDetailedViewData({ ...filterData, ...searchPayload });
                    }}
                  />
                  <ActionFilter
                    parentClass="op-drop-down"
                    id="op-drop-down"
                    text={
                      <i className="bi bi-three-dots-vertical text-16-400 color-dark-silver" />
                    }
                    elem={elem}
                    handleSuccess={() => {
                      getDetailedViewData({ ...filterData, ...searchPayload });
                    }}
                    isOpen={abstractId === elem?.id}
                    onToggle={() => handleToggle(elem?.id)}
                  />
                </>
              )}
            </>
          </span>
        ),
      },
    ];
    rowData.push({ data: obj });
  });
  return (
    // <Card className="p-2 unset-br">
    <>
      <div className="d-flex justify-content-between align-items-center cpt-24 cpb-26 flex-wrap gap-2">
        <div className="table-title">Detailed View ({filterData?.total})</div>
        <div className="d-flex flex-row flex-wrap gap-3">
          {searchPayload?.statusType && (
            <div className="d-flex align-items-center flex-wrap gap-2">
              <span className="text-14-500 color-0000">You Selected:</span>
              <span className="text-14-400 color-new-car b-new-car rounded h-35 d-flex align-items-center ps-2 pe-2 bg-new-car-light">
                {`${searchPayload?.statusType} : ${searchPayload?.statuss}`}
              </span>
            </div>
          )}
          {!eventId &&
            <div className="text-14-400 grey-border d-flex align-items-center gap-2 ps-2 pe-2">
              <CheckBox
                type="PRIMARY-ACTIVE"
                onClick={() => {
                  setIsLoading(true);
                  let newData = { ...filterData, ...searchPayload };
                  newData = {
                    ...newData,
                    temporary_users: filterData.temporary_users ? '' : '1',
                  };
                  setFilterData(newData);
                  getDetailedViewData(newData);
                }}
                isChecked={filterData?.temporary_users === '1'}
              />
              <div>Temporary ID</div>
            </div>}
          <ExportButton
            exportAPI={exportSubmittedAbstracts}
            payload={objectToFormData({
              ...searchPayload,
              temporary_users: filterData?.temporary_users,
              membership_plan_id: filterData?.membership_plan_id,
              type: "detailedView"
            })}
          />
          {/* {access?.isFilter && ( */}
          <div className="d-flex">
            <FilterDropdown
              list={membershipList}
              handelChangeFilter={handelChangeFilter}
            />
          </div>
          {/* )} */}
        </div>
      </div>
      <div >
        <Table
          isLoading={isLoading}
          header={header}
          rowData={rowData}
          filterData={filterData}
          searchPayload={searchPayload}
          searchInputChange={handelChangeSearch}
          changeOffset={handelChangePagination}
          {...(!eventId && { isOtherPagination: true })}
          submittedAbstracts
          maxHeight="60vh"
        />
      </div>
    </>
    // </Card>
  );
};

export default DetailedView;
